import type Result from 'true-myth/result'

import type { ClientWapiError } from './wapi-client'

export interface LogoutDeps {
    /** The user's session token from the mirror, or null if absent. */
    token: string | null
    /** Client-direct POST to WAPI `/v2/logout.json` with the user token. */
    logoutRequest: (token: string) => Promise<Result<void, ClientWapiError>>
    /** Remove the localStorage `userAccessToken` mirror. */
    clearMirror: () => void
    /** Snowplow `setUserId` — called with `null` to clear identity. */
    setSnowplowUserId: (id: string | null) => void
    /** Datadog RUM `clearUser`. */
    clearRumUser: () => void
}

/**
 * Log out in place. Tells WAPI to invalidate the session (best-effort — a failed
 * call must not strand the user logged-in locally), then unconditionally clears the
 * local session: the mirror and the Snowplow/RUM identity. Never navigates and never
 * throws. Fires **no** structured event, matching ui-react's `logOut` (its
 * `postLogOutCleanUp` is just `setUserId(null)` + `clearUser`).
 */
export async function performLogout(deps: LogoutDeps): Promise<void> {
    try {
        if (deps.token) {
            // The outcome is deliberately discarded: the user logs out locally whether or
            // not WAPI could be reached.
            await deps.logoutRequest(deps.token)
        }
    } finally {
        // `finally`, not `catch`: clearing the local session is the part a logged-out
        // user is entitled to, so it cannot be conditional on the network call — while a
        // collaborator that breaks its no-reject contract still surfaces rather than
        // leaving a visitor who clicked Log Out silently signed in.
        deps.clearMirror()
        deps.setSnowplowUserId(null)
        deps.clearRumUser()
    }
}
